// Canonical frequency is an annualized float (0.2 = 1-in-5-year); the scenario form stores a
// times/years Int pair. These convert losslessly and deliberately do NOT clamp the percentage,
// so values >= 1 render as > 100% (unlike FrequencyIndicator).

/** Sentinel chosenSourceId for the triangulated suggestion. FE-only; dropped at the wire. */
export const TRIANGULATED = 'triangulated';

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

export interface TimesYears {
  times: number;
  years: number;
}

/** Express an annualized rate as a GCD-reduced { times, years } pair (no >= 1 special-case). */
export const annualizedToTimesYears = (value: number): TimesYears => {
  if (!isFinite(value) || value <= 0) return { times: 0, years: 1 };
  // Below 1%, the /100 form rounds the denominator wrong (or to "0 events"): 0.8% would read
  // "1 in 100" (≈1%). Represent sub-1% rates accurately as 1-in-N years so the denominator matches
  // the shown % chance. At/above 1% keep the /100 form (1% = 1-in-100, 3% = 3-in-100, …).
  if (value < 0.01) return { times: 1, years: Math.max(1, Math.round(1 / value)) };
  const times = Math.round(value * 100);
  const years = 100;
  const divisor = gcd(times, years) || 1;
  return { times: times / divisor, years: years / divisor };
};

export const timesYearsToAnnualized = (times: number, years: number): number =>
  years === 0 ? 0 : times / years;

/**
 * Annual chance as a display string with 3 significant figures — never rounds a non-zero rate down
 * to "0" (a 0.001% chance must read as "0.001%", not "0%"). Returns a string (may carry decimals).
 */
export const annualChancePercent = (value: number): string => {
  const pct = value * 100;
  if (!isFinite(pct) || pct <= 0) return '0';
  const digits = Math.ceil(Math.log10(pct));
  const magnitude = Math.pow(10, 3 - digits);
  return String(Math.round(pct * magnitude) / magnitude);
};

export const formatFrequency = (value: number): string => {
  const { times, years } = annualizedToTimesYears(value);
  const eventWord = times === 1 ? 'event' : 'events';
  const yearWord = years === 1 ? 'year' : 'years';
  return `${times} ${eventWord} in ${years} ${yearWord} (${annualChancePercent(value)}% annual chance)`;
};

/** Display-only: smallest sensible denominator approximating the value, paired with the exact %. */
export const niceFrequencyDisplay = (value: number): string => {
  if (!isFinite(value) || value <= 0) return formatFrequency(value);

  let best = { times: 0, years: 0, err: Infinity };
  for (let years = 1; years <= 12; years += 1) {
    const times = Math.round(value * years);
    if (times <= 0) continue;
    const err = Math.abs(times / years - value);
    if (err < best.err) best = { times, years, err };
    if (err <= 0.02) break; // close enough — prefer the smallest denominator
  }

  // No fit within 12 years (a sub-~4% rate) — fall back to formatFrequency, which now renders the
  // exact 1-in-N denominator (and 3-sig-fig %) for tiny rates rather than flooring to 1-in-100.
  if (best.err === Infinity) return formatFrequency(value);

  const eventWord = best.times === 1 ? 'event' : 'events';
  const yearWord = best.years === 1 ? 'year' : 'years';
  return `${best.times} ${eventWord} in ${best.years} ${yearWord} (${annualChancePercent(value)}% annual chance)`;
};
